import type {
  LeanCustomsDeclaration,
  LeanCustomsDeclarationItem,
  LeanCustomsInvoice,
} from "../services/leancustomsService";

function parseMoney(v: unknown): number | null {
  if (v == null) return null;
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const cleaned = v
      .replace(/EUR|€/gi, "")
      .replace(/\s/g, "")
      .replace(",", ".");
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function formatEur(amount: number): string {
  return new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR",
  }).format(amount);
}

/** Sum numeric values on invoice lines whose key matches (Lean “Payment” / “Surety” columns). */
function sumInvoiceLineFields(
  invoices: LeanCustomsInvoice[],
  keyTest: (keyLower: string) => boolean
): number | null {
  let sum = 0;
  let found = false;
  for (const inv of invoices) {
    for (const line of inv.invoice_lines || []) {
      const rec = line as Record<string, unknown>;
      for (const [k, v] of Object.entries(rec)) {
        const kl = k.toLowerCase();
        if (!keyTest(kl)) continue;
        if (kl.includes("status") || kl.includes("method")) continue;
        const n = parseMoney(v);
        if (n != null) {
          sum += n;
          found = true;
        }
      }
    }
  }
  return found ? sum : null;
}

function paymentColumnKeyTest(kl: string): boolean {
  if (kl.includes("surety") || kl.includes("guarantee") || kl.includes("bail")) {
    return false;
  }
  return (
    kl === "payment" ||
    kl.includes("payment_amount") ||
    (kl.includes("payment") && !kl.includes("repayment"))
  );
}

function suretyColumnKeyTest(kl: string): boolean {
  return (
    kl.includes("surety") ||
    kl.includes("guarantee") ||
    kl.includes("bail") ||
    (kl.includes("deposit") && !kl.includes("prepayment"))
  );
}

const DECLARATION_PAYMENT_KEYS = [
  "total_payment_amount",
  "total_payment",
  "payment_total",
  "amount_to_pay",
  "payable_amount",
  "utb_total",
  "import_payment_total",
];

const DECLARATION_SURETY_KEYS = [
  "total_surety_amount",
  "total_surety",
  "surety_total",
  "guarantee_total",
  "ttw_total",
  "total_guarantee",
];

function topLevelAmount(
  declaration: LeanCustomsDeclaration | null,
  keys: string[]
): number | null {
  if (!declaration) return null;
  const rec = declaration as Record<string, unknown>;
  const lowerKeyToValue = new Map(
    Object.entries(rec).map(([k, v]) => [k.toLowerCase(), v])
  );
  for (const want of keys) {
    const v = lowerKeyToValue.get(want.toLowerCase());
    const n = parseMoney(v);
    if (n != null) return n;
  }
  return null;
}

export interface UtbTtwAmountDisplay {
  /** Total payment (UTB / “Payment” in Lean) */
  utbPayment: string | null;
  /** Total surety (TTW / “Surety” in Lean) */
  ttwSurety: string | null;
}

/**
 * Amounts from live LeanCustoms JSON: declaration invoices (line columns) and
 * common top-level declaration fields. Exact names vary by API version.
 */
export function extractUtbTtwAmountDisplays(
  declaration: LeanCustomsDeclaration | null,
  invoices: LeanCustomsInvoice[]
): UtbTtwAmountDisplay {
  const linePay = sumInvoiceLineFields(invoices, paymentColumnKeyTest);
  const lineSur = sumInvoiceLineFields(invoices, suretyColumnKeyTest);
  const pay = linePay ?? topLevelAmount(declaration, DECLARATION_PAYMENT_KEYS);
  const sur = lineSur ?? topLevelAmount(declaration, DECLARATION_SURETY_KEYS);

  return {
    utbPayment: pay != null ? formatEur(pay) : null,
    ttwSurety: sur != null ? formatEur(sur) : null,
  };
}

// ---- Duty breakdown from declaration items (for invoice customs lines) ----

export interface DutyBreakdown {
  /** Import VAT (BTW bij Invoer) — Dexwork type code B00 or similar B-prefixed */
  btwBijInvoer: number;
  /** Import duties (Invoerrechten) — Dexwork type code A1xx or similar A-prefixed */
  invoerrechten: number;
}

/**
 * Extract separate BTW bij Invoer and Invoerrechten amounts from declaration
 * items' `duty_tax_fees`. In the Dexwork/LeanCustoms API:
 *   - A-prefixed type codes = customs duty (invoerrechten)
 *   - B-prefixed type codes = import VAT (BTW bij invoer)
 *
 * Falls back to heuristic field-name matching if type_code is absent.
 */
export function extractDutyBreakdown(
  items: LeanCustomsDeclarationItem[]
): DutyBreakdown {
  let btwBijInvoer = 0;
  let invoerrechten = 0;

  for (const item of items) {
    if (!Array.isArray(item.duty_tax_fees)) continue;

    for (const fee of item.duty_tax_fees) {
      const rec = fee as Record<string, unknown>;
      const typeCode = String(rec.type_code ?? rec.code ?? rec.type ?? "").toUpperCase();

      // LeanCustoms splits amounts into surety (TTW / Zekerheid) and payment
      // (UTB / Verschuldigd). Only Verschuldigd belongs on the customer invoice.
      const payment = parseMoney(rec.payment);
      const amount =
        payment ??
        parseMoney(rec.payable_amount ?? rec.amount ?? rec.tax_amount ?? rec.value);
      if (amount == null || amount <= 0) continue;

      if (typeCode.startsWith("B")) {
        btwBijInvoer += amount;
      } else if (typeCode.startsWith("A")) {
        invoerrechten += amount;
      } else {
        // Heuristic: check description/name fields
        const desc = String(rec.description ?? rec.name ?? "").toLowerCase();
        if (desc.includes("btw") || desc.includes("vat") || desc.includes("omzetbelasting")) {
          btwBijInvoer += amount;
        } else {
          invoerrechten += amount;
        }
      }
    }
  }

  return {
    btwBijInvoer: Math.round(btwBijInvoer * 100) / 100,
    invoerrechten: Math.round(invoerrechten * 100) / 100,
  };
}
